import { apiClient } from '../utils/apiClient';
import { sanitizeRichTextHtml } from '../utils/sanitizeHtml';

/**
 * In-app reports — the Settings → About "Report an issue" flow.
 *
 * Deliberately NOT `contactService`. That one posts to the public
 * `/contact-messages` endpoint the website form uses, where the reporter has to
 * type a name and an email as free text; this one is authenticated, so the
 * server takes the reporter from the session and the identity cannot be
 * spoofed. They are two queues on purpose: contact is sales/support, reports
 * are the bug tracker.
 *
 * Attachments are uploaded FIRST and referenced by url in the submit. The
 * server refuses any url that is not under its own storage base, so a report
 * cannot smuggle an arbitrary link into the admin dashboard — meaning the
 * upload step is not an optimisation here, it is the only path that produces a
 * url the submit will accept.
 */

/** Mirrors the server's cap; the real enforcement is server-side. */
export const MAX_ATTACHMENTS = 5;
/** Bytes. The server caps at 25 MB — failing here first saves a doomed upload. */
export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

/**
 * The diagnostics the server will keep.
 *
 * It filters to a fixed key set (`app_version os arch region locale desktop`)
 * and drops the rest, so sending more is silently discarded — this list exists
 * so the client does not collect, and transmit, information that will be thrown
 * away. Nothing here identifies a machine: no hostname, no serial, no hardware
 * id. A bug report should not become a fingerprint.
 */
export async function collectDiagnostics({ desktop } = {}) {
  const diagnostics = {
    desktop: desktop ? 'true' : 'false',
    locale: typeof navigator !== 'undefined' ? navigator.language || '' : '',
  };

  if (desktop) {
    try {
      const [{ getVersion }, os] = await Promise.all([
        import('@tauri-apps/api/app'),
        import('@tauri-apps/plugin-os'),
      ]);
      diagnostics.app_version = await getVersion();
      diagnostics.os = typeof os.platform === 'function' ? await os.platform() : '';
      diagnostics.arch = typeof os.arch === 'function' ? await os.arch() : '';
    } catch {
      // Diagnostics are a convenience for triage, never a precondition for
      // reporting a bug. A failure here must not block the report.
    }
  }

  return diagnostics;
}

/** Topics and limits, so the client's dropdown cannot drift from the server. */
export async function fetchMeta() {
  const { data } = await apiClient.get('/reports/meta');
  return data;
}

/**
 * Upload one attachment, returning the metadata to send with the report.
 *
 * `name`/`mime`/`size` are sent alongside the bytes because the server records
 * the declared facts rather than trusting the multipart filename.
 */
export async function uploadAttachment(file) {
  const form = new FormData();
  form.append('file', file);
  form.append('name', file.name || 'attachment');
  form.append('mime', file.type || 'application/octet-stream');
  form.append('size', String(file.size ?? 0));

  const { data } = await apiClient.post('/reports/attachments', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

/**
 * Submit a report.
 *
 * The body is sanitised here as well as on the server. That is not redundancy
 * for its own sake: the server sanitises because a client is not a trust
 * boundary (anyone can curl the endpoint), and the client sanitises because
 * whatever it sends is what the user believes they sent. Neither substitutes
 * for the other.
 */
export async function submitReport({ topic, subject, bodyHtml, diagnostics, attachments }) {
  const { data } = await apiClient.post('/reports', {
    topic,
    subject: (subject || '').trim(),
    body_html: sanitizeRichTextHtml(bodyHtml || ''),
    diagnostics: diagnostics || {},
    attachments: attachments || [],
  });
  return data;
}

/** Human-readable reason a file cannot be attached, or null when it can. */
export function attachmentRejection(file, currentCount) {
  if (currentCount >= MAX_ATTACHMENTS) return 'too_many';
  if ((file?.size ?? 0) > MAX_ATTACHMENT_BYTES) return 'too_large';
  return null;
}

const reportService = {
  collectDiagnostics,
  fetchMeta,
  uploadAttachment,
  submitReport,
  attachmentRejection,
  MAX_ATTACHMENTS,
  MAX_ATTACHMENT_BYTES,
};

export default reportService;
